import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  ArrowDownLeft,
  CheckCircle,
  Clock,
  Eye,
  Smartphone,
  XCircle,
  RefreshCw,
  Check,
  X,
  AlertCircle,
} from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { formatCurrency, formatDate, formatTimeIST, getInitials, exportToCSV } from '@/lib/utils';
import toast from 'react-hot-toast';
import { depositsApi } from '@/lib/api';

interface DepositProof {
  screenshotUrl: string;
  utrNumber: string;
  payerUpiId: string;
  submittedAt: string;
}

interface Deposit {
  _id: string;
  userId: {
    _id: string;
    name: string;
    phone: string;
    email?: string;
    avatar?: string;
  };
  amount: number;
  status: 'pending' | 'awaiting_proof' | 'completed' | 'failed' | 'cancelled';
  paymentMethod: string;
  depositProof?: DepositProof;
  description?: string;
  createdAt: string;
  completedAt?: string;
  failureReason?: string;
  verifiedBy?: {
    name: string;
    email: string;
  };
  verifiedAt?: string;
}

interface Stats {
  pending: number;
  completed: number;
  failed: number;
  totalAmount: number;
}

export default function Deposits() {
  const [deposits, setDeposits] = useState<Deposit[]>([]);
  const [stats, setStats] = useState<Stats>({ pending: 0, completed: 0, failed: 0, totalAmount: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Modal states
  const [selectedDeposit, setSelectedDeposit] = useState<Deposit | null>(null);
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [verifyAction, setVerifyAction] = useState<'approve' | 'reject'>('approve');
  const [verifyNotes, setVerifyNotes] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const itemsPerPage = 15;

  const fetchDeposits = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: itemsPerPage,
      };

      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }

      const response = await depositsApi.getAll(params);
      const { deposits: fetchedDeposits, stats: fetchedStats, pagination } = response.data.data;

      setDeposits(fetchedDeposits || []);
      setStats(fetchedStats || { pending: 0, completed: 0, failed: 0, totalAmount: 0 });
      setTotalPages(pagination?.pages || 1);
      setTotalItems(pagination?.total || 0);
    } catch (error: any) {
      console.error('Failed to fetch deposits:', error);
      toast.error('Failed to load deposits');
    } finally {
      setLoading(false);
    }
  }, [currentPage, statusFilter]);

  useEffect(() => {
    fetchDeposits();
  }, [fetchDeposits]);

  const handleVerify = async () => {
    if (!selectedDeposit) return;

    if (verifyAction === 'reject' && !verifyNotes.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setVerifying(true);
    try {
      await depositsApi.verify(selectedDeposit._id, {
        action: verifyAction,
        notes: verifyNotes.trim() || undefined,
      });

      toast.success(
        verifyAction === 'approve'
          ? `Deposit of ₹${selectedDeposit.amount} approved and credited!`
          : 'Deposit rejected'
      );

      setShowVerifyModal(false);
      setSelectedDeposit(null);
      setVerifyNotes('');
      fetchDeposits();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to verify deposit');
    } finally {
      setVerifying(false);
    }
  };

  const openVerifyModal = (deposit: Deposit, action: 'approve' | 'reject') => {
    setSelectedDeposit(deposit);
    setVerifyAction(action);
    setVerifyNotes('');
    setShowVerifyModal(true);
  };


  const handleExport = () => {
    exportToCSV(
      deposits.map((d) => ({
        ID: d._id,
        User: d.userId?.name || 'N/A',
        Phone: d.userId?.phone || 'N/A',
        Amount: d.amount,
        Status: d.status,
        'Has Screenshot': d.depositProof?.screenshotUrl ? 'Yes' : 'No',
        'Created At': d.createdAt,
      })),
      'deposits'
    );
    toast.success('Deposits exported successfully');
  };

  // Filter deposits by search (client-side for instant feedback)
  const filteredDeposits = deposits.filter((d) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      d.userId?.name?.toLowerCase().includes(searchLower) ||
      d.userId?.phone?.includes(search) ||
      d.depositProof?.utrNumber?.toLowerCase().includes(searchLower) ||
      d._id.toLowerCase().includes(searchLower)
    );
  });

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'awaiting_proof':
        return 'info';
      case 'failed':
      case 'cancelled':
        return 'danger';
      default:
        return 'default';
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold">Deposits</h1>
          <p className="text-slate-400 mt-1">Manage and verify user deposit requests</p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={fetchDeposits} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card variant="glass" className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <ArrowDownLeft className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-400">{formatCurrency(stats.totalAmount)}</p>
              <p className="text-sm text-slate-400">Total Deposits</p>
            </div>
          </div>
        </Card>

        <Card variant="glass" className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-violet-400">{stats.completed}</p>
              <p className="text-sm text-slate-400">Completed</p>
            </div>
          </div>
        </Card>

        <Card variant="glass" className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-400">{stats.pending}</p>
              <p className="text-sm text-slate-400">Pending Verification</p>
            </div>
          </div>
        </Card>

        <Card variant="glass" className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-400">{stats.failed}</p>
              <p className="text-sm text-slate-400">Failed/Rejected</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Filters */}
      <Card variant="glass" className="p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search by user, phone, UTR, or transaction ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              icon={<Search className="w-4 h-4" />}
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-4 py-2 rounded-lg bg-slate-900/50 border border-slate-700 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500/50"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending Verification</option>
              <option value="awaiting_proof">Awaiting Proof</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed/Rejected</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Deposits Table */}
      <Card variant="glass" className="overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-500"></div>
          </div>
        ) : filteredDeposits.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No deposits found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                      User
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                      Amount
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                      Screenshot
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider text-slate-400">
                      Date
                    </th>
                    <th className="px-6 py-4 text-right text-xs font-semibold uppercase tracking-wider text-slate-400">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {filteredDeposits.map((deposit) => (
                    <tr key={deposit._id} className="hover:bg-slate-800/30 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center text-white text-xs font-semibold">
                            {getInitials(deposit.userId?.name || 'U')}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{deposit.userId?.name || 'Unknown'}</p>
                            <p className="text-xs text-slate-500">{deposit.userId?.phone || 'N/A'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-semibold text-emerald-400">
                          +{formatCurrency(deposit.amount)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {deposit.depositProof?.screenshotUrl ? (
                          <button
                            onClick={() => setImagePreview(deposit.depositProof!.screenshotUrl)}
                            className="group relative"
                          >
                            <img
                              src={deposit.depositProof.screenshotUrl}
                              alt="Payment proof"
                              className="w-12 h-12 rounded-lg object-cover border border-slate-700 group-hover:border-violet-400 transition-colors"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = 'https://via.placeholder.com/48?text=Err';
                              }}
                            />
                            <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <Eye className="w-4 h-4 text-white" />
                            </div>
                          </button>
                        ) : (
                          <span className="text-slate-500 text-sm">No proof</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant={getStatusBadgeVariant(deposit.status) as any}>
                          {deposit.status.replace('_', ' ')}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm">{formatDate(deposit.createdAt)}</p>
                          <p className="text-xs text-slate-500">{formatTimeIST(deposit.createdAt)}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => setSelectedDeposit(deposit)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          {deposit.status === 'pending' && deposit.depositProof && (
                            <>
                              <Button
                                variant="default"
                                size="sm"
                                onClick={() => openVerifyModal(deposit, 'approve')}
                                className="bg-emerald-600 hover:bg-emerald-500"
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => openVerifyModal(deposit, 'reject')}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-800">
              <p className="text-sm text-slate-400">
                Showing {(currentPage - 1) * itemsPerPage + 1} to{' '}
                {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Detail Modal */}
      <Dialog open={!!selectedDeposit && !showVerifyModal} onOpenChange={() => setSelectedDeposit(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Deposit Details</DialogTitle>
            <DialogDescription>Transaction ID: {selectedDeposit?._id}</DialogDescription>
          </DialogHeader>
          {selectedDeposit && (
            <div className="py-4 space-y-6">
              {/* Amount & Status */}
              <div className="flex items-center justify-between pb-4 border-b border-slate-800">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                    <ArrowDownLeft className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-emerald-400">
                      +{formatCurrency(selectedDeposit.amount)}
                    </p>
                    <p className="text-sm text-slate-400">{selectedDeposit.paymentMethod || 'Manual UPI'}</p>
                  </div>
                </div>
                <Badge variant={getStatusBadgeVariant(selectedDeposit.status) as any} className="h-fit">
                  {selectedDeposit.status.replace('_', ' ')}
                </Badge>
              </div>

              {/* User Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400">User</p>
                  <p className="font-medium">{selectedDeposit.userId?.name || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-400">Phone</p>
                  <p className="font-medium">{selectedDeposit.userId?.phone || 'N/A'}</p>
                </div>
              </div>

              {/* Deposit Proof */}
              {selectedDeposit.depositProof?.screenshotUrl && (
                <div className="p-4 bg-slate-800/50 rounded-lg space-y-4">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Smartphone className="w-4 h-4 text-violet-400" />
                    Payment Proof
                  </h3>
                  <div>
                    <p className="text-sm text-slate-400 mb-2">Screenshot</p>
                    <button
                      onClick={() => setImagePreview(selectedDeposit.depositProof!.screenshotUrl)}
                      className="block hover:opacity-80 transition-opacity cursor-pointer"
                    >
                      <img
                        src={selectedDeposit.depositProof.screenshotUrl}
                        alt="Payment Screenshot"
                        className="max-h-64 rounded-lg object-contain bg-white/5 border border-slate-700 hover:border-violet-400 transition-colors"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/200x150?text=Image+Error';
                        }}
                      />
                    </button>
                    <p className="text-xs text-violet-400 mt-2 cursor-pointer hover:text-violet-300">Click to enlarge</p>
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-400">Created At</p>
                  <p className="font-medium">{formatDate(selectedDeposit.createdAt)}</p>
                </div>
                {selectedDeposit.verifiedAt && (
                  <div>
                    <p className="text-sm text-slate-400">Verified At</p>
                    <p className="font-medium">{formatDate(selectedDeposit.verifiedAt)}</p>
                  </div>
                )}
              </div>

              {/* Failure Reason */}
              {selectedDeposit.failureReason && (
                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <p className="text-sm text-red-400">
                    <strong>Rejection Reason:</strong> {selectedDeposit.failureReason}
                  </p>
                </div>
              )}

              {/* Action Buttons */}
              {selectedDeposit.status === 'pending' && selectedDeposit.depositProof && (
                <div className="flex gap-3 pt-4 border-t border-slate-800">
                  <Button
                    className="flex-1 bg-emerald-600 hover:bg-emerald-500"
                    onClick={() => openVerifyModal(selectedDeposit, 'approve')}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Approve Deposit
                  </Button>
                  <Button
                    variant="destructive"
                    className="flex-1"
                    onClick={() => openVerifyModal(selectedDeposit, 'reject')}
                  >
                    <X className="w-4 h-4 mr-2" />
                    Reject Deposit
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Verify Confirmation Modal */}
      <Dialog open={showVerifyModal} onOpenChange={setShowVerifyModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${verifyAction === 'approve' ? 'text-emerald-400' : 'text-red-400'}`}>
              {verifyAction === 'approve' ? (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Approve Deposit
                </>
              ) : (
                <>
                  <XCircle className="w-5 h-5" />
                  Reject Deposit
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              {verifyAction === 'approve'
                ? `This will credit ₹${selectedDeposit?.amount} to the user's wallet.`
                : 'The user will be notified that their deposit was rejected.'}
            </DialogDescription>
          </DialogHeader>

          {selectedDeposit && (
            <div className="py-4 space-y-4">
              {/* Summary */}
              <div className="p-4 bg-slate-800/50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-slate-400">Amount</span>
                  <span className="font-bold text-lg">{formatCurrency(selectedDeposit.amount)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-slate-400">User</span>
                  <span>{selectedDeposit.userId?.name}</span>
                </div>
              </div>

              {/* Notes Input */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  {verifyAction === 'approve' ? 'Notes (optional)' : 'Rejection Reason *'}
                </label>
                <textarea
                  value={verifyNotes}
                  onChange={(e) => setVerifyNotes(e.target.value)}
                  placeholder={
                    verifyAction === 'approve'
                      ? 'Add any notes for this approval...'
                      : 'Please provide a reason for rejection...'
                  }
                  className="w-full px-4 py-3 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-violet-500/50 focus:border-transparent resize-none"
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter className="gap-3">
            <Button variant="outline" onClick={() => setShowVerifyModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleVerify}
              disabled={verifying || (verifyAction === 'reject' && !verifyNotes.trim())}
              className={verifyAction === 'approve' ? 'bg-emerald-600 hover:bg-emerald-500' : ''}
              variant={verifyAction === 'reject' ? 'destructive' : 'default'}
            >
              {verifying ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : verifyAction === 'approve' ? (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Approve & Credit
                </>
              ) : (
                <>
                  <X className="w-4 h-4 mr-2" />
                  Reject
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Image Preview Modal */}
      <Dialog open={!!imagePreview} onOpenChange={() => setImagePreview(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Payment Screenshot</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-center py-4">
            <img
              src={imagePreview || ''}
              alt="Payment Screenshot"
              className="max-h-[70vh] object-contain rounded-lg"
            />
          </div>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
